/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import 'bootstrap/dist/css/bootstrap.min.css';
import { useState, useEffect, useContext } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from 'styled-components';
import { getAiOps, deleteAiOp, AiOp } from '../data';
import { StatusContext, StatusContextType } from './StatusContext';
import AiOpsTable from './AiOpsTable';

function AiOps() {
    const [aiOps, setAiOps] = useState<AiOp[]>([]);
    const [reload, setReload] = useState<boolean>(false);
    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
    const navigate = useNavigate();
    const statusContext = useContext<StatusContextType>(StatusContext);
    const theme: any = useTheme();

    useEffect(() => {
        async function fetchAiOps() {
            try {
                const data = await getAiOps();
                setAiOps(data);
                setReload(false);
            } catch (error: any) {
                statusContext.postError(`Error fetching AI Operations: ${error.message}`);
            }
        }

        fetchAiOps();
    }, [reload]);

    const doDelete = async (id: string) => {
        const op = aiOps.find(op => op.id === id);
        if (!op) return;
        
        try {
            await deleteAiOp(id);
            statusContext.postError(`Operation "${op.description}" deleted successfully`);
            setReload(true);
            setRowSelection({});
        } catch (error: any) {
            statusContext.postError(`Failed to delete AI Operation: ${error.message}`);
        }
    };

    return (
        <Container fluid style={{ backgroundColor: theme.colors.tableBg, color: theme.colors.ctext, height: 'calc(100vh-40px-20px)', width: '100%' }}>
            <Row className="mb-3">
                <Col>
                    <h3>
                        AI Operations
                        <Button 
                            variant="primary" 
                            className="mx-2" 
                            onClick={() => navigate('/ai/edit_op/new')}
                            style={{
                                backgroundColor: theme.colors.primary || '#0d6efd',
                                borderColor: theme.colors.primary || '#0d6efd'
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                        </Button>
                        
                        {Object.keys(rowSelection).length > 0 && (
                            <Button 
                                variant="danger" 
                                className="mx-2" 
                                onClick={() => {
                                    if (window.confirm(`Delete ${Object.keys(rowSelection).length} selected operations?`)) {
                                        Object.entries(rowSelection).forEach(([id, selected]) => {
                                            if (selected) {
                                                doDelete(id);
                                            }
                                        });
                                    }
                                }}
                                style={{
                                    backgroundColor: theme.colors.danger || '#dc3545',
                                    borderColor: theme.colors.danger || '#dc3545'
                                }}
                            >
                                <FontAwesomeIcon icon={faMinus} />
                            </Button>
                        )}
                    </h3>
                </Col>
            </Row>
            <Row>
                <Col>
                    <AiOpsTable 
                        data={aiOps} 
                        setReload={setReload}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                    />
                </Col>
            </Row>
        </Container>
    );
}

export default AiOps;